import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, Fragment as _Fragment, createElementBlock as _createElementBlock, renderList as _renderList, createTextVNode as _createTextVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withCtx as _withCtx, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "terms" }
const _hoisted_2 = { class: "page-content" }
const _hoisted_3 = { class: "page-title" }
const _hoisted_4 = { class: "text" }
const _hoisted_5 = { class: "form-group" }
const _hoisted_6 = { class: "radbox" }
const _hoisted_7 = { class: "radbox__text" }
const _hoisted_8 = ["onUpdate:modelValue"]
const _hoisted_9 = { class: "radbox__text" }
const _hoisted_10 = ["href"]
const _hoisted_11 = {
  key: 0,
  class: "input-error"
}
const _hoisted_12 = {
  key: 2,
  class: "form-error"
}
const _hoisted_13 = ["placeholder"]
const _hoisted_14 = {
  key: 3,
  class: "input-error"
}
const _hoisted_15 = { class: "text terms__email-text" }

import FeedbackModal from "@/components/ui/FeedbackModal"
import { GuestChargingService } from "@/services/guest-charging-service"
import {
  email,
  maxLength,
  minLength,
  required,
  sameAs
} from "@vuelidate/validators"
import config from "../config/index"
import PageHeader from "../components/ui/PageHeader.vue"
import LoadingComponent from "../components/ui/LoadingComponent.vue"
import ButtonComponent from "../components/ui/ButtonComponent.vue"
import { useVuelidate } from "@vuelidate/core"
import { useReCaptcha } from "vue-recaptcha-v3"
import { computed, onMounted, reactive, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useDictionaryStore } from "../store/dictionary"
import { storeToRefs } from "pinia"
import { useUserStore } from "../store/user"

type FormType = {
  email: string
  [key: string]: string | boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'TermsPage',
  setup(__props) {

const { locale, t } = useI18n()
const router = useRouter()
const recaptcha = useReCaptcha()
const dictionaryStore = useDictionaryStore()
const {
  getRegistrationDocuments: registrationDocuments,
  getTermsDataIsLoading: termsDataIsLoading,
  dynamicConfig
} = storeToRefs(dictionaryStore)
const userStore = useUserStore()
const {
  acceptedTerms,
  email: storedEmail,
  seenLandingPage
} = storeToRefs(userStore)
const { setAcceptedTerms, setEmail, setTempToken } = userStore

const form = reactive<FormType>({
  email: ""
})
const acceptAll = ref(false)
const recaptchaToken = ref<string | undefined>("")
const emailErrorMessageTitle = ref("")
const emailErrorMessageSubtitle = ref("")
const isFeedbackModalActive = ref(false)
const submitIsLoading = ref(false)

const registrationDocumentsIds = computed(() => {
  return registrationDocuments.value.map((document) => document.documentId)
})

const acceptAllEnabled = computed(() => {
  return registrationDocumentsIds.value.length > 1
})

const checkboxes = computed(() => {
  return Object.fromEntries(
    Object.entries(form).filter(([key]) => key !== "email")
  )
})

const rules = computed(() => {
  return {
    form: {
      email: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(60),
        email: email
      },
      ...Object.fromEntries(
        registrationDocumentsIds.value.map((id) => [
          id,
          { required, sameAs: sameAs(true) }
        ])
      )
    }
  }
})

const v$ = useVuelidate(rules, { form })

watch(acceptAll, (newValue) => {
  const checkboxValues = Object.values(checkboxes.value)
  const areAllCheckboxValuesEqual = new Set(checkboxValues).size <= 1

  // If acceptAll get's unchecked and all checkbox values are not equal, acceptAll field was set progmatically in "checkboxes" watcher and not by user
  if (!newValue && !areAllCheckboxValuesEqual) {
    return
  }

  registrationDocumentsIds.value.forEach((id) => {
    form[id] = newValue
  })
})

watch(registrationDocumentsIds, (ids) => {
  ids.forEach((id) => {
    form[id] = false
  })
})

watch(locale, (newValue) => {
  dictionaryStore.fetchRegistrationDocuments(newValue)
  acceptAll.value = false
})

watch(
  checkboxes,
  (newValue) => {
    acceptAll.value = Object.values(newValue).every((value) => value)
  },
  { deep: true }
)

dictionaryStore.fetchRegistrationDocuments(locale.value)

const setUser = () => {
  return {
    languagePreference: locale.value,
    isTermsAndConditionsAccepted: acceptedTerms.value,
    email: storedEmail.value
  }
}

const feedbackPopUp = () => {
  isFeedbackModalActive.value = true
}

const handleFeedbackModalOkClick = () => {
  isFeedbackModalActive.value = false
}

const getRecaptchaToken = async () => {
  await recaptcha?.recaptchaLoaded() // Wait for reCAPTCHA to load
  return await recaptcha?.executeRecaptcha("generateMobilitiUser") // Execute reCAPTCHA with action "generateMobilitiUser"
}

const nextClicked = async () => {
  recaptchaToken.value = await getRecaptchaToken()
  recaptcha?.instance?.value?.showBadge()
  setAcceptedTerms(true)
  setEmail(form.email)

  try {
    submitIsLoading.value = true
    const resp = await GuestChargingService.generateMobilitiUser(
      setUser(),
      recaptchaToken.value
    )

    if (!resp?.data?.temporaryCode) {
      feedbackPopUp()
      return
    }

    setTempToken(resp.data.temporaryCode)

    // If all reg fields are hidden
    if (Object.values(config.registrationFields).every((value) => !value)) {
      setTimeout(async () => {
        const accountingResp = await GuestChargingService.setAccountingInfo({})

        submitIsLoading.value = false

        window.location.href = accountingResp.data.simplePayUrl
      }, 5000)

      return
    }

    submitIsLoading.value = false

    router.push("registration")
  } catch (e: any) {
    console.error(e.response)
    console.error("onSubmit error: ", e.response.data)

    submitIsLoading.value = false

    if (e.response.data.errorMessage === "user.emailAlreadyInUse") {
      emailErrorMessageTitle.value = t(
        "errors.error400.userEmailAlreadyInUseTitle"
      )
      emailErrorMessageSubtitle.value = t(
        "errors.error400.userEmailAlreadyInUseSubtitle"
      )
    }

    feedbackPopUp()
  }
}

onMounted(() => {
  if (!seenLandingPage.value) {
    console.log("User has not been landing page! Redirecting..")
    router.push("/")
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(PageHeader),
    _createElementVNode("main", _hoisted_2, [
      _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t("termsPage.title")), 1),
      _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t("termsPage.subTitle")), 1),
      _createElementVNode("form", {
        onSubmit: _withModifiers(nextClicked, ["prevent"])
      }, [
        _createElementVNode("div", _hoisted_5, [
          (_unref(termsDataIsLoading))
            ? (_openBlock(), _createBlock(LoadingComponent, { key: 0 }))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (acceptAllEnabled.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("label", _hoisted_6, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((acceptAll).value = $event)),
                          class: "radbox__input",
                          type: "checkbox"
                        }, null, 512), [
                          [_vModelCheckbox, acceptAll.value]
                        ]),
                        _createElementVNode("span", _hoisted_7, _toDisplayString(_ctx.$t("termsPage.acceptAll")), 1)
                      ]),
                      _cache[2] || (_cache[2] = _createElementVNode("hr", { class: "form-separator" }, null, -1))
                    ], 64))
                  : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(registrationDocuments), (document, index) => {
                  return (_openBlock(), _createElementBlock("label", {
                    key: index,
                    class: "radbox"
                  }, [
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": ($event: any) => ((_unref(v$).form[document.documentId].$model) = $event),
                      class: "radbox__input",
                      type: "checkbox"
                    }, null, 8, _hoisted_8), [
                      [_vModelCheckbox, _unref(v$).form[document.documentId].$model]
                    ]),
                    _createElementVNode("span", _hoisted_9, [
                      _createTextVNode(_toDisplayString(document.prefix) + " ", 1),
                      _createElementVNode("a", {
                        href: document.url,
                        target: "_blank",
                        class: "radbox__link"
                      }, _toDisplayString(document.documentTitle), 9, _hoisted_10),
                      _createTextVNode(_toDisplayString(document.suffix) + " * ", 1),
                      (_unref(v$).form[document.documentId].$error)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_ctx.$t("termsPage.validation.checkbox")), 1))
                        : _createCommentVNode("", true)
                    ])
                  ]))
                }), 128))
              ], 64)),
          (_unref(dynamicConfig))
            ? (_openBlock(), _createElementBlock("p", _hoisted_12, [
                _createTextVNode(_toDisplayString(_ctx.$t("termsPage.notification1", {
                advanceFee: _unref(dynamicConfig).prepayAmount,
                currency: _unref(config).currency
              })) + " ", 1),
                _cache[3] || (_cache[3] = _createElementVNode("br", null, null, -1)),
                (_unref(dynamicConfig).guestCommission > 0)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createTextVNode(_toDisplayString(_ctx.$t("termsPage.notification2", {
                  handlingFee: _unref(dynamicConfig).guestCommission,
                  currency: _unref(config).currency
                })), 1)
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createTextVNode(_toDisplayString(_ctx.$t("termsPage.notificationCardRegistrationCharge", {
                  cardRegistrationChargeFee: _unref(dynamicConfig).registrationCharge,
                  currency: _unref(config).currency
                })), 1)
                    ], 64))
              ]))
            : _createCommentVNode("", true),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(v$).form.email.$model) = $event)),
            class: _normalizeClass(["input terms__email-input", { error: _unref(v$).form.email.$error }]),
            type: "text",
            placeholder: _unref(config).emailPlaceholder
          }, null, 10, _hoisted_13), [
            [_vModelText, _unref(v$).form.email.$model]
          ]),
          (_unref(v$).form.email.$error)
            ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(_ctx.$t("termsPage.validation.email", { minLength: 5, maxLength: 60 })), 1))
            : _createCommentVNode("", true),
          _createElementVNode("p", _hoisted_15, _toDisplayString(_ctx.$t("termsPage.emailDescription")), 1),
          _createVNode(ButtonComponent, {
            block: "",
            primary: "",
            disabled: _unref(v$).form.$invalid || _unref(termsDataIsLoading),
            loading: submitIsLoading.value,
            onClick: nextClicked
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t("termsPage.nextButton")), 1)
            ]),
            _: 1
          }, 8, ["disabled", "loading"])
        ])
      ], 32)
    ]),
    (isFeedbackModalActive.value)
      ? (_openBlock(), _createBlock(_unref(FeedbackModal), {
          key: 0,
          "main-title": 
        emailErrorMessageTitle.value
          ? emailErrorMessageTitle.value
          : _ctx.$t('errors.defaultError.mainTitle')
      ,
          "sub-title": 
        emailErrorMessageSubtitle.value
          ? emailErrorMessageSubtitle.value
          : _ctx.$t('errors.defaultError.subTitle')
      ,
          success: false,
          onConfirm: handleFeedbackModalOkClick
        }, null, 8, ["main-title", "sub-title"]))
      : _createCommentVNode("", true)
  ]))
}
}

})